import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ReactComponent as UploadIcon } from '../../assets/cloud-upload.svg';
import { ReactComponent as DownloadIcon } from '../../assets/dp-download.svg';
import { ReactComponent as TickIcon } from '../../assets/tick-icon-dp.svg';
import { ReactComponent as SuccessTicketIcon } from '../../assets/icon-vector.svg';
import { ReactComponent as WarningIcon } from '../../assets/warning-dp-job.svg';
import { ReactComponent as ImportIcon } from '../../assets/dp-upload-icon.svg';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import { Column } from 'react-data-grid';
import * as XLSX from 'xlsx';

interface BulkUploadDialogProps<T extends Record<string, any>> {
  readonly open: boolean;
  readonly onCloseAction: (showQueryBuilder?: boolean) => void;
  readonly title?: string;
  readonly steps?: string[];
  readonly columns: Array<Column<T>>;
  readonly rowKey: string;
  readonly sampleFileName?: string;
  readonly sampleHeaders?: string[];
  readonly sampleRows?: Array<Record<string, string>>;
  readonly normalizeRows?: (rows: Array<Record<string, any>>, indexOffset?: number) => T[];
  readonly validateRow?: (row: T) => boolean;
  readonly parseFile?: (file: File) => Promise<Array<Record<string, any>>>;
  readonly onApplyUpdates?: (rows: T[]) => Promise<void> | void;
  readonly onAddMore?: () => void;
  readonly uploadTitle?: string;
  readonly uploadTooltip?: string;
  readonly dropzoneMessage?: string;
  readonly acceptedFileExtensions?: string[];
  readonly acceptedMimeTypes?: Record<string, string[]>;
  readonly downloadButtonLabel?: string;
  readonly reUploadButtonLabel?: string;
  readonly cancelButtonLabel?: string;
  readonly actionButtonLabel?: string;
  readonly reUploadButtonSx?: Record<string, any>;
  readonly cancelButtonSx?: Record<string, any>;
  readonly actionButtonSx?: Record<string, any>;
  readonly showValidOnlyLabel?: string;
  readonly successTitle?: (validCount: number) => string;
  readonly successDescription?: string;
  readonly rowClass?: (row: T) => string;
}

interface DragDropFileUploadProps {
  onDropFiles: (files: File[]) => void;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  acceptedFileExtensions?: string[];
  acceptedMimeTypes?: Record<string, string[]>;
  dropzoneMessage?: string;
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const StyledStepIcon = styled('div')(({ active, completed }: { active: boolean; completed?: boolean }) => ({
  width: '34px',
  height: '34px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  backgroundColor: active || completed ? '#EFF6FF' : '#71758F',
  color: '#003771',
  fontWeight: 'bold',
  transition: 'all 0.3s ease',
  '& svg path': {
    fill: '#003771',
  },
}));

interface StepIconProps {
  active: boolean;
  completed: boolean;
  index: number;
}

function StepIconForward({ active, completed, index }: StepIconProps) {
  return completed ? (
    <StyledStepIcon active={active} completed>
      <TickIcon />
    </StyledStepIcon>
  ) : (
    <StyledStepIcon active={active}>{index + 1}</StyledStepIcon>
  );
}

function getStepIconComponent(index: number, activeStep: number) {
  return function StepIcon(props: any) {
    return (
      <StepIconForward
        active={props.active}
        completed={activeStep > index}
        index={index}
      />
    );
  };
}

function normalizeBulkRow<T extends Record<string, any>>(row: T, index: number, validateRow?: (row: T) => boolean): T {
  const record = row as Record<string, any>;

  return {
    ...record,
    id: record.id ?? index,
    isValid: record.isValid ?? validateRow?.(row) ?? true,
    selected: record.selected ?? false,
  } as unknown as T;
}

const DragDropFileUpload = (props: DragDropFileUploadProps) => {
  const { onDropFiles, onFileUpload, acceptedFileExtensions = ['.csv', '.xlsx'], acceptedMimeTypes, dropzoneMessage } = props;
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (onDropFiles) onDropFiles(acceptedFiles);
    },
    [onDropFiles],
  );

  const acceptMap = acceptedMimeTypes ?? acceptedFileExtensions.reduce((map, extension) => {
    const normalized = extension.toLowerCase();

    if (normalized === '.xlsx') {
      map['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'] = map['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'] || [];
      map['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'].push('.xlsx');
    }

    if (normalized === '.csv') {
      map['text/csv'] = map['text/csv'] || [];
      map['text/csv'].push('.csv');
    }

    return map;
  }, {} as Record<string, string[]>);

  const { getRootProps } = useDropzone({
    onDrop,
    accept: acceptMap,
    multiple: false,
  });

  const message = dropzoneMessage || `Drop ${acceptedFileExtensions.join(' or ')} file or`;
  const acceptedExtensionsLabel = acceptedFileExtensions.join(' or ');

  return (
    <Box {...getRootProps()}>
      <div
        style={{
          width: '100%',
          maxWidth: '680px',
          minHeight: '240px',
          borderRadius: '4px',
          border: '1px dashed rgba(113, 117, 143, 1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          boxSizing: 'border-box',
          backgroundColor: 'rgba(240, 248, 255, 1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px', maxWidth: '100%' }}>
          <UploadIcon style={{ width: '120px', height: '120px' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Typography style={{ color: 'rgba(113, 117, 143, 1)', whiteSpace: 'nowrap' }}>
              {message || `Drop ${acceptedExtensionsLabel} file or`}
            </Typography>
            <Button component="label" variant="text"  sx={{ p: 0, minWidth: 0, lineHeight: 1 }}>
              <Typography style={{ cursor: 'pointer', color: 'rgba(0, 155, 216, 1)', textDecoration: 'underline', fontWeight: 500 }}>
                browse from here
                <VisuallyHiddenInput
                  type="file"
                  accept={acceptedFileExtensions.join(',')}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => onFileUpload(event)}
                />
              </Typography>
            </Button>
          </Box>
        </Box>
      </div>
    </Box>
  );
};

const StyledConnector = styled(StepConnector)(({ completed }: { completed: boolean }) => ({
  '&.MuiStepConnector-root': {
    top: 22,
    width: '50px',
    flex: 'none',
  },
  '& .MuiStepConnector-line': {
    borderColor: completed ? '#EFF6FF' : '#71758F',
    borderTopWidth: 2,
    transition: 'all 0.3s ease',
  },
}));

const StyledLabel = styled(Typography)(({ active, completed }: { active: boolean; completed?: boolean }) => {
  let colorValue;
  if (active || completed) {
    colorValue = '#EFF6FF';
  } else {
    colorValue = '#71758F';
  }
  return {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: colorValue,
    transition: 'all 0.3s ease',
  };
});

function BulkUploadDialog<T extends Record<string, any>>(props: Readonly<BulkUploadDialogProps<T>>) {
  const {
    open,
    onCloseAction,
    title = 'Bulk Upload',
    steps = ['Upload File', 'Confirm & Add'],
    columns,
    rowKey,
    sampleFileName = 'sample_template.csv',
    sampleHeaders = [],
    sampleRows = [],
    normalizeRows,
    validateRow,
    parseFile,
    onApplyUpdates,
    onAddMore,
    uploadTitle = 'Upload a File to Import',
    uploadTooltip = 'Upload a file to import',
    dropzoneMessage,
    acceptedFileExtensions = ['.csv', '.xlsx'],
    acceptedMimeTypes,
    downloadButtonLabel = 'Download Sample Files',
    reUploadButtonLabel = 'Re-upload File',
    cancelButtonLabel = 'CANCEL',
    actionButtonLabel = 'FORCE UPDATE',
    reUploadButtonSx,
    cancelButtonSx,
    actionButtonSx,
    showValidOnlyLabel = 'Show valid records only',
    successTitle = (validCount) => `Records have been updated for ${validCount} rows successfully.`,
    successDescription = 'Would you like to add more?',
    rowClass,
  } = props;
  const [activeStep, setActiveStep] = useState(0);
  const [rows, setRows] = useState<T[]>([]);
  const [showValidOnly, setShowValidOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const parseCSVFile = async (file: File) => {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length < 2) {
      return [];
    }

    const headers = lines[0].split(',').map((header) => header.trim());
    return lines.slice(1).map((line) => {
      const values = line.split(',').map((value) => value.trim());
      return headers.reduce((row: Record<string, any>, header: string, index: number) => {
        row[header] = values[index] ?? '';
        return row;
      }, {});
    });
  };

  const handleFileUpload = useCallback(async (files: File[]) => {
    if (!files || !files[0]) {
      return;
    }

    const file = files[0];
    setValidationErrors([]);
    setRows([]);
    setIsSuccess(false);

    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    const allowedExtensions = acceptedFileExtensions.map((ext) => ext.replace(/^[.]/, '').toLowerCase());

    if (!allowedExtensions.includes(extension)) {
      setValidationErrors([`Invalid file type. Please upload ${acceptedFileExtensions.join(' or ')} file.`]);
      return;
    }

    if (file.size === 0) {
      setValidationErrors(['Uploaded file is empty. Please provide a valid file.']);
      return;
    }

    let parsedRows: Array<Record<string, any>> = [];

    try {
      if (parseFile) {
        parsedRows = await parseFile(file);
      } else if (extension === 'csv') {
        parsedRows = await parseCSVFile(file);
      } else {
        setValidationErrors([`Unsupported file type. Please upload ${acceptedFileExtensions.join(' or ')} file.`]);
        return;
      }
    } catch (err) {
      setValidationErrors([`Failed to parse file. ${err instanceof Error ? err.message : ''}`.trim()]);
      return;
    }

    if (!parsedRows.length) {
      setValidationErrors(['Uploaded file is empty or contains no valid rows.']);
      return;
    }

    const normalizedSourceRows = normalizeRows ? normalizeRows(parsedRows) : (parsedRows as unknown as T[]);
    const normalizedRows = normalizedSourceRows.map((row, index) => normalizeBulkRow(row, index, validateRow));

    setRows(normalizedRows);
    setActiveStep(1);
  }, [acceptedFileExtensions, normalizeRows, parseFile, validateRow]);

  const convertToCSV = (data: Array<Record<string, any>>) => {
    if (!data || data.length === 0) {
      return '';
    }

    const rows = data.map((row) => sampleHeaders.map((header) => row[header] ?? '').join(','));
    return [sampleHeaders.join(','), ...rows].join('\n');
  };

  const convertToXLSXBlob = (data: Array<Record<string, any>>) => {
    const worksheet = XLSX.utils.json_to_sheet(data, { header: sampleHeaders });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  };

  const handleSampleFileDownload = () => {
    if (!sampleHeaders.length) {
      return;
    }

    const rows = sampleRows.map((row) => sampleHeaders.reduce((acc: Record<string, any>, header) => {
      acc[header] = row[header] ?? '';
      return acc;
    }, {} as Record<string, any>));

    const isXlsx = sampleFileName.toLowerCase().endsWith('.xlsx');
    const blob = isXlsx ? convertToXLSXBlob(rows) : new Blob([convertToCSV(rows)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', sampleFileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoke asynchronously to avoid potential race conditions
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);
  };

  const displayData = showValidOnly ? rows.filter((row) => row.isValid !== false) : rows;
  const canForceUpdate = displayData.length > 0 && displayData.every((row) => row.isValid !== false);

  const handleReset = () => {
    setActiveStep(0);
    setRows([]);
    setShowValidOnly(false);
    setValidationErrors([]);
    setIsLoading(false);
    setIsSuccess(false);
  };

  const handleCloseInternal = (showQueryBuilder = false) => {
    handleReset();
    onCloseAction(showQueryBuilder);
  };

  const handleDialogClose = (_event: {}, _reason: 'backdropClick' | 'escapeKeyDown') => {
    handleCloseInternal(false);
  };

const handleApplyUpdates = async () => {
  setIsLoading(true);
  setIsSuccess(false);
  try {
    const validRows = rows.filter((row) => row.isValid !== false);
    await onApplyUpdates?.(validRows);  
    setIsSuccess(true);
  } catch (err) {
    console.error('BulkUploadDialog: onApplyUpdates failed', err);
  } finally {
    setIsLoading(false);  
  }
};

  const handleAddMore = () => {
    if (onAddMore) {
      onAddMore();
      return;
    }

    handleReset();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '4px',
          backgroundColor: 'rgba(245, 248, 252, 1)',
        },
      }}
    >
      <DialogTitle bgcolor="primary.main" sx={{ p: '20px 30px !important' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <Typography sx={{ fontWeight: 600, color: 'white', fontSize: '24px', whiteSpace: 'nowrap' }}>
            {title}
          </Typography>
          <Box display="flex" alignItems="center" gap={1} flex={1}>
            <Stepper activeStep={activeStep} style={{ width: '500px', justifyContent: 'end', padding: '0px', marginLeft: 'auto' }} connector={<StyledConnector completed={activeStep > 0} />}>
              {steps.map((label, index) => {
                const isActive = index === activeStep;
                return (
                  <Step key={label}>
                    <StepLabel
                      StepIconComponent={getStepIconComponent(index, activeStep)}>
                      <StyledLabel active={isActive} completed={activeStep > index}>
                        {label}
                      </StyledLabel>
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
            <IconButton aria-label="Close dialog" onClick={() => handleCloseInternal(false)} sx={{ p: 0 }}>
              <CloseIcon fontSize="medium" sx={{ color: '#ffffff' }} />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, px: 2, backgroundColor: 'rgba(245, 248, 252, 1)' }}>
        <Box sx={{ p: 0 }}>
          {activeStep === 0 && (
            <Box>
              <Box sx={{ px: '14px', pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Typography sx={{ fontWeight: 600, color: '#009BD8', fontSize: '18px' }}>
                  {uploadTitle}
                </Typography>
                <Tooltip title={uploadTooltip} placement="right">
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <InfoOutlinedIcon style={{ width: '24px', height: '24px', opacity: 0.6, color: "#71758F" }} />
                  </span>
                </Tooltip>
                <Box sx={{ flex: 1, height: '1px', backgroundColor: '#009BD8' }} />
              </Box>
              <Box sx={{ px: '14px', pb: 2 }}>
                <DragDropFileUpload
                  onDropFiles={(files: File[]) => handleFileUpload(files)}
                  onFileUpload={(event) => handleFileUpload(event.target.files ? Array.from(event.target.files) : [])}
                  acceptedFileExtensions={acceptedFileExtensions}
                  acceptedMimeTypes={acceptedMimeTypes}
                  dropzoneMessage={dropzoneMessage}
                />
              </Box>
              <Box sx={{ px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                {validationErrors.length > 0 && (
                  <Box sx={{ width: '100%', p: 2, backgroundColor: 'rgba(255, 229, 229, 1)', borderRadius: '4px' }}>
                    {validationErrors.map((message) => (
                      <Typography key={message} sx={{ color: 'rgba(185, 28, 28, 1)', fontSize: '0.9rem', mb: 0.5 }}>
                        {message}
                      </Typography>
                    ))}
                  </Box>
                )}
                <Button
                  startIcon={<DownloadIcon />}
                  variant="outlined"
                  size="small"
                  onClick={handleSampleFileDownload}
                  disabled={!sampleHeaders.length}
                  sx={{
                    fontSize: '16px',
                    color: 'rgba(0, 155, 216, 1)',
                    borderColor: 'rgba(0, 155, 216, 1)',
                    backgroundColor: 'white',
                    '&:hover': {
                      borderColor: 'rgba(0, 155, 216, 1)',
                      backgroundColor: 'rgba(0, 155, 216, 0.05)',
                    },
                  }}
                >
                  {downloadButtonLabel}
                </Button>
              </Box>
            </Box>
          )}

          {activeStep === 1 && !isLoading && !isSuccess && (
            <Grid container spacing={2} sx={{ margin: 0, width: '100%' }}>
              {rows.some((row) => row.isValid === false) && (
                <Grid item xs={12} sx={{ padding: '0px 20px 0px 20px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, backgroundColor: 'rgba(255, 162, 0, 0.15)', borderRadius: '4px', color: 'rgba(255, 162, 0, 1)' }}>
                    <WarningIcon style={{ marginRight: '10px' }} />
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
                      Unable to read {rows.filter((r) => r.isValid === false).length} Records due to errors. Those records cannot be added.
                    </Typography>
                  </Box>
                </Grid>
              )}

              <Grid item xs={12} sx={{ padding: '40px 20px 0px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Grid item xs={9}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '22px', marginBottom: '5px' }}>
                    <Typography sx={{ color: 'rgba(15, 55, 100, 1)', display: 'flex', alignItems: 'center', fontSize: '18px', fontWeight: '600' }}>
                      Records Preview
                    </Typography>
                    <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(0, 155, 216, 1)', marginLeft: '16px' }} />
                  </Box>
                </Grid>
                <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <FormControlLabel
                    sx={{
                      marginRight: 0,
                      '& .MuiFormControlLabel-label': { color: 'rgba(15, 55, 100, 1) !important', fontWeight: 500, marginLeft: '6px' },
                      '& .MuiCheckbox-root': { color: 'rgba(15, 55, 100, 1) !important', padding: '3px' },
                      '& .MuiCheckbox-root.Mui-disabled': { color: 'rgba(15, 55, 100, 1) !important', opacity: 1 },
                    }}
                    control={
                      <Checkbox
                        checked={showValidOnly}
                        onChange={(e) => setShowValidOnly(e.target.checked)}
                        icon={
                          <Box sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '3px',
                            border: '2px solid rgba(15, 55, 100, 1)',
                            backgroundColor: 'transparent',
                          }} />
                        }
                        checkedIcon={
                          <Box sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '3px',
                            backgroundColor: 'rgba(15, 55, 100, 1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}>
                            <TickIcon style={{ width: 10, height: 10, color: 'white' }} />
                          </Box>
                        }
                        sx={{
                          padding: 0,
                          '&.Mui-disabled': { opacity: 1 },
                        }}
                      />
                    }
                    label={showValidOnlyLabel}
                  />
                </Grid>
              </Grid>

              <Grid item xs={12} sx={{ padding: '10px 20px 0px 20px' }}>
                <Box sx={{
                  '& .rdg': {
                    '&::-webkit-scrollbar': {
                      width: '12px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(0, 155, 216, 1)',
                      borderRadius: '8px',
                      border: '3px solid transparent',
                      backgroundClip: 'content-box',
                    },
                    scrollbarColor: 'rgba(0,155,216,1) transparent',
                      '& .bulk-upload-invalid-row': {
                        backgroundColor: 'rgba(255, 247, 214, 0.9) !important',
                      },
                      '& .bulk-upload-invalid-row .rdg-cell': {
                        borderLeft: '4px solid rgba(255, 193, 7, 0.9)',
                      },
                  }
                }}>
                  <CustomizedDataGrid
                    tableColumns={columns}
                    tableData={displayData}
                    rowKey={rowKey}
                    tableType="bulk-upload"
                    defaultColumnNames={columns.map((col) => col.name)}
                    isRenderPagination={false}
                    isDateRangeFilter={false}
                    isDisabled={false}
                    style={{ height: '30vh', width: '100%', minWidth: '100%' }}
                    rowClass={rowClass}
                  />
                </Box>
              </Grid>

              {displayData.length > 0 && (
                <Grid item xs={12} sx={{ padding: '0px 20px 0px 20px' }}>
                  <Typography sx={{ color: 'rgba(113, 117, 143, 1)', fontStyle: 'italic', fontSize: '14px', fontWeight: 500 }}>
                    Showing 1-{displayData.length} of {displayData.length} results
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0px 20px 20px 20px' }}>
                <Grid>
                  <Button
                    component="label"
                    variant="pepwiseSecondaryOutline2"
                    sx={{
                      width: '200px',
                      color: 'rgba(0, 155, 216, 1)',
                      borderColor: 'rgba(0, 155, 216, 1)',
                      ...reUploadButtonSx,
                    }}
                  >
                    <ImportIcon style={{ marginRight: '10px', marginBottom: '3px' }} />{' '}
                    {reUploadButtonLabel}
                    <VisuallyHiddenInput
                      type="file"
                      accept={acceptedFileExtensions.join(',')}
                      onChange={(event) => {
                        handleFileUpload(event.target.files ? Array.from(event.target.files) : []);
                        event.target.value = '';
                      }}
                    />
                  </Button>
                </Grid>
                <Grid>
                  <Button
                    variant="text"
                    onClick={handleReset}
                    sx={{
                      color: 'rgba(0, 155, 216, 1)',
                      mr: 1,
                      textTransform: 'none',
                      width: '100px',
                      ...cancelButtonSx,
                    }}
                  >
                    {cancelButtonLabel}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleApplyUpdates}
                    disabled={!canForceUpdate}
                    sx={{ 
                      width: '170px', 
                      textTransform: 'none', 
                      color: '#39ACE7', 
                      borderColor: '#39ACE7',
                      "&.Mui-disabled": {
                        color: '#94A3B8 !important',
                        borderColor: '#94A3B8 !important',
                        opacity: '1 !important',
                        WebkitTextFillColor: '#94A3B8 !important',
                      },
                      ...actionButtonSx }}
                  >
                    {actionButtonLabel}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          )}

          {isLoading && activeStep === 1 && (
            <Box sx={{ p: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  border: '4px solid rgba(0, 155, 216, 0.2)',
                  borderTopColor: 'rgba(0, 155, 216, 1)',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
              <Typography sx={{ fontWeight: 600, color: '#78818c' }}>
                Capturing the results. 
                <br />
                Please wait for a while...
              </Typography>
            </Box>
          )}

          {isSuccess && activeStep === 1 && (
            <Box sx={{ p: 4 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={2}>
                  <Box sx={{ width: '65px', height: '65px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <SuccessTicketIcon />
                  </Box>
                </Grid>
                <Grid item xs={10}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.2, color: 'rgba(15, 55, 100, 1)' }}>
                    {successTitle(rows.filter((row) => row.isValid !== false).length)}
                  </Typography>
                  <Typography sx={{ color: 'rgba(113, 117, 143, 1)', mt: 1, fontSize: '1rem' }}>
                    {successDescription}
                  </Typography>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                <Button variant="outlined" onClick={() => handleCloseInternal(true)} sx={{ textTransform: 'none' }}>
                  SAVE & CLOSE
                </Button>
                <Button variant="contained" onClick={handleAddMore} sx={{ textTransform: 'none' }}>
                  ADD MORE
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default BulkUploadDialog;
